// "Which way now?" — the cleared front, grown a choice (PLAN §19.4).
//
// The moment the game already stops for. A front turns over, the board sits in
// mate waiting for `next-stage`, and instead of a camp simply arriving the
// player is handed two ways the board could grow. Nothing new about *when* the
// game pauses, which §19.4 names as most of the risk.
//
// **This replaces the cleared-front celebration rather than joining it.** The
// two would otherwise be modals back to back saying much the same thing: the
// picker already reports what the front cost, and by the time a camp has
// landed the player chose it. `GameShell` turns the stage card off wherever
// this is on, so exactly one plate speaks for a front.
//
// The plate is `Celebration.tsx`'s, imported rather than rebuilt, for the
// reason that file gives: a game that announces its moments in unrelated
// visual languages reads as two games. What differs is that there is something
// to *decide*.
//
// Four rules carried over, every one already paid for here:
//
//  - **Never `window.confirm`.** It blocks the main thread, it is the
//    browser's chrome rather than the game's, and Playwright cannot reach one.
//  - **Nothing may reflow the canvas.** The plate's `disableScrollLock`.
//  - **Never animate a button's `transform`.** An infinitely animated button
//    never holds still and Playwright cannot click it. Nothing here pulses.
//  - **It takes the pointer.** A click landing on the board behind it would be
//    aimed at something the player cannot see.

import { useEffect } from "react";

import type { Branch } from "src/api";
import { Detail, Kicker, PlateShell, Reward, StatRow, Stats } from "./Celebration";
import { kitRarity } from "src/engine/session/hand";
import { HoverCard, motion, styled, Text, useTheme } from "./kit";
import { RARITY_NAME } from "src/engine/session/rarity";

/**
 * The offers, side by side.
 *
 * **One row, however many there are.** `auto-fit` with a minimum wrapped the
 * third offer onto its own line the moment a front had one — and the whole
 * claim of this panel is that the offers are alternatives, which a card
 * sitting under the other two stops looking like.
 *
 * `1fr` columns rather than content-width, so a camp with a long name is not
 * visibly the bigger button — a recommendation nobody wrote.
 *
 * Exported with `Offer` below because `ObservePicker` is the same panel with a
 * sentence where the axes are, exactly as the two shared one stylesheet.
 */
export const Offers = styled("div")(({ theme }) => ({
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: "1fr",
  gap: theme.kit.space.sm,
  width: "100%",
  marginTop: theme.kit.space.md
}));

/**
 * One offer.
 *
 * **Nothing here animates `transform`.** Playwright waits for an element's box
 * to hold still, and a button that breathes is a button a smoke times out on.
 * The hover lift is a border and a background, never a move.
 *
 * The rarity arrives as `--rarity` so the hover rule can lean on it without
 * knowing which step it is — and so a card and a stage offer wearing the same
 * rarity are the same colour by construction rather than by two tables
 * agreeing.
 */
export const Offer = styled("button")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs,
  padding: `${theme.kit.space.md}px ${theme.kit.space.md}px`,
  font: "inherit",
  textAlign: "left",
  color: theme.kit.color.ink,
  background: theme.kit.color.panelRaised,
  border: `2px solid var(--rarity, ${theme.kit.color.line})`,
  borderRadius: theme.kit.radius.md,
  boxShadow: "inset 0 0 0 1px color-mix(in srgb, var(--rarity, transparent) 35%, transparent)",
  cursor: "pointer",
  ...motion([], ["background", "box-shadow"]),
  "&:hover": {
    background: `color-mix(in srgb, ${theme.kit.color.lineHi} 22%, ${theme.kit.color.panelRaised})`,
    boxShadow:
      "inset 0 0 0 1px var(--rarity, transparent), 0 0 12px color-mix(in srgb, var(--rarity, transparent) 40%, transparent)"
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.kit.color.brand}`,
    outlineOffset: 2
  }
}));

/** The offer's name — the heading voice, since it names a thing. */
export const OfferName = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 16,
  color: theme.kit.color.cream
}));

/** The line under it: what kind of thing this is. */
export const OfferHeading = styled("span")(({ theme }) => ({
  fontFamily: theme.kit.font.display,
  fontSize: 10,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: theme.kit.color.inkDim
}));

/**
 * The axes, as a fixed column of rows.
 *
 * So two offers read as a table rather than as two paragraphs of different
 * lengths — the whole claim of this panel is that they are alternatives.
 */
const Marks = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  marginTop: theme.kit.space.xs
}));

const Mark = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  gap: theme.kit.space.md,
  fontSize: 12,
  color: theme.kit.color.inkDim
}));

export function BranchPickerOverlay({
  view,
  stage,
  onPick
}: {
  view: Branch;
  /** The front about to open, counting from one. */
  stage: number;
  onPick(index: number): void;
}) {
  const { color, rarity } = useTheme().kit;

  // **Digits pick, and nothing else does.**
  //
  // Deliberately not the celebration's "any key takes it": there are two
  // outcomes and neither is obvious, so a single key that chose for you would
  // throw away the decision the panel exists to ask for. And no Escape — this
  // has no dismiss, because the board is sitting in mate until a camp lands.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const index = Number(event.key) - 1;
      if (!Number.isInteger(index) || index < 0 || index >= view.offers.length) return;
      event.preventDefault();
      onPick(index);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onPick, view.offers.length]);

  return (
    <PlateShell
      testid="branch-picker"
      plateTestid="branch-plate"
      label="Choose where the board grows"
    >
      <Kicker role="display" tone="brand" size={12} weight={700}>
        Front cleared
      </Kicker>
      <Reward>{view.only ? "One way on" : "Choose your ground"}</Reward>

      {/* What the front cost. The same numbers the stage card reported, from
          the same `statsOf` — this *is* that card now, so they have to be the
          same numbers rather than a second opinion. */}
      <Stats data-testid="branch-stats">
        {view.stats.map((stat) => (
          <StatRow key={stat.id} {...stat} />
        ))}
      </Stats>

      <Offers data-testid="branch-offers">
        {[
          ...view.offers,
          // A board with no room for a front is offered the recovery.
          ...(view.grow ? [view.grow] : [])
        ].map((offer) => (
          // The border says how unusual this is, and nothing else does — a
          // word beside it would be spending a line on what a colour states
          // instantly. So the card carries the *rarity* and nothing the tile
          // has already said: repeating the title and the detail put a copy of
          // the offer on top of the offer. `bottom`, because these are a row.
          <HoverCard
            key={offer.id}
            placement="bottom"
            title={RARITY_NAME[offer.rarity]}
            card="How unusual this offer is."
          >
            <Offer
              type="button"
              data-testid={offer.id}
              // In `style`, not a styled variant: five rarities across three
              // offers is data, and emotion mints a class per distinct value.
              style={{ ["--rarity" as string]: rarity[kitRarity(offer.rarity)] }}
              onClick={() => onPick(offer.index)}
              // **The first is the safe one**, and it is focused so a player
              // who answers with the keyboard cannot land on the adventurous
              // offer by default (§19.4).
              autoFocus={offer.index === 0}
            >
              <OfferName>{offer.title}</OfferName>
              <OfferHeading>{offer.heading}</OfferHeading>

              <Marks>
                {offer.marks.map((mark) => (
                  <Mark key={mark.axis}>
                    <Text tone="dim" size={12}>
                      {mark.label}
                    </Text>
                    {/* `edge` is a difference, never a verdict — it tints the
                        row and says nothing about which offer is better. More
                        of an axis is a harder fight *and* a richer one, so the
                        two tints are warm and cool rather than good and bad,
                        and `fight` is already inverted in the model so the cool
                        one always means "easier here". */}
                    <Text
                      role="display"
                      size={12}
                      weight={700}
                      style={{
                        color:
                          mark.edge === 1
                            ? color.cream
                            : mark.edge === -1
                              ? color.brand
                              : color.ink
                      }}
                    >
                      {mark.value}
                    </Text>
                  </Mark>
                ))}
              </Marks>

              {offer.detail && (
                <Text tone="dim" size={12} sx={{ mt: 0.25, lineHeight: 1.35 }}>
                  {offer.detail}
                </Text>
              )}
            </Offer>
          </HoverCard>
        ))}
      </Offers>

      {/* Said out loud, because it is true by construction and a player has
          no way to find it out except by trying: Undo takes back this turn's
          own actions and floors at its start (§29 U2), so a branch taken
          mid-run is permanent (§19.6 hazard 7). A re-rollable choice is not a
          choice. */}
      <Detail tone="dim" size={12} sx={{ maxWidth: "40ch" }}>
        {view.only
          ? `The board opens one way here. Stage ${stage} lands where it can.`
          : "This cannot be undone."}
      </Detail>
    </PlateShell>
  );
}
